import React, {
  useState, useEffect, useRef, useCallback
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Typography,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import { MAX_RECORDING_SIZE_BYTES } from 'src/helpers/azure';

/* ***************************************************************************
 * VoiceRecorder — CON-1674
 *
 * Records a voice memo in the compose box and hands the parent a File.
 *
 * WHY IT PRODUCES WEBM/MP4 AND NOT MP3. No browser can record MP3 — verified
 * in Edge on 2026-09-03, MediaRecorder.isTypeSupported('audio/mpeg') is false.
 * Chrome and Firefox give webm/opus, Safari mp4/AAC, and no carrier accepts
 * either. The server converts to 32 kbps mono MP3 at send time. So this
 * component deliberately does NOT care what container it captures; it picks
 * whatever the browser supports and lets the server settle the format.
 *
 * THE 120 SECOND CAP AUTO-STOPS RATHER THAN REFUSING. A consultant who talks
 * for two minutes keeps what they said; they do not lose it to a validation
 * error. 120s at 32 kbps is ~470 KB, which AT&T delivered byte-for-byte on
 * 2026-09-02.
 *
 * ONE COMPONENT, TWO MENUS. The chat compose box uses InputStartAdornment and
 * every other compose surface uses AttachmentField — they are separate menus
 * that happen to look alike. This component is the shared part so the two menu
 * items are the only duplication.
 *************************************************************************** */

export const MAX_RECORDING_SECONDS = 120;

// Below this many seconds remaining, the cue turns amber. Far enough out that
// a consultant can wrap up rather than be cut off mid-sentence.
const WARN_REMAINING_SECONDS = 20;

/* The recorder hands over a chunk this often. It exists so bytes can be
 * METERED while recording rather than measured once at the end — see
 * shouldStopForBytes. One second is coarse enough to be cheap and fine
 * enough that the last chunk before the ceiling is small. */
export const RECORDER_TIMESLICE_MS = 1000;

/* ***************************************************************************
 * shouldStopForBytes
 *
 * THE FAILURE MODE THIS CLOSES. The recorder used to stop at 120 s and the
 * size check ran AFTERWARDS, so a consultant who spoke for two minutes was
 * refused and lost all of it. Raising the ceiling makes that unlikely; it
 * does not make it impossible — the next time a browser changes its default
 * bitrate, the arithmetic changes with it. So the recording is made to end
 * on WHICHEVER LIMIT COMES FIRST, seconds or bytes, and a post-hoc rejection
 * has nowhere left to come from.
 *
 * The rule: stop now if one more chunk the size of the last one would cross
 * the ceiling. That stops one chunk EARLY, which is the correct side to err
 * on — the alternative is capturing a chunk and then finding it does not
 * fit. `bytes >= ceiling` is checked on its own so a zero-length chunk (some
 * browsers emit an empty first one) cannot make the prediction think the
 * next chunk is free.
 *************************************************************************** */
export const shouldStopForBytes = ({ bytes, lastChunk, ceiling }) => (
  bytes >= ceiling || bytes + lastChunk > ceiling
);

/* ***************************************************************************
 * CON-1720 — the floor. Below this, there is no recording to hand over.
 *
 * WHY. Two Production sends, six days apart and from different users,
 * uploaded a ZERO-BYTE webm. Both passed every check we had — 0 < 500,000 is
 * true — uploaded without complaint, and failed only when ffmpeg opened them.
 * The user was then told the file "could not be converted", which is the
 * wrong story: nothing was recorded in the first place. Both were the user's
 * first recording after a gap of days, and the retry a minute later worked.
 *
 * WHY 1,024 AND NOT 0. What ffmpeg rejects is a file with no audio FRAMES,
 * and that is not only zero bytes: a webm/opus or mp4 container header with
 * nothing inside it (EBML + Segment + Tracks, or ftyp + moov with no mdat) is
 * a few hundred bytes and fails identically. Meanwhile one second of real
 * audio is 4,000 B at the lowest browser default (32 kbps) and 16,000 B at
 * Chrome's measured 128 kbps. 1,024 sits between the two by a wide margin —
 * above any header-only file, 4–16x below one second of speech. Nothing
 * under it can be a memo.
 *
 * WHY BYTES AND NOT SECONDS. A silent microphone still produces bytes; Opus
 * encodes silence. Zero bytes means the encoder never delivered a frame at
 * all, and that can happen at any wall-clock duration. Bytes is the signal.
 *
 * WHY HERE AND NOT THE PARENT. The recorder has the size first, it already
 * has onError as its channel for "this did not work", and a guard here
 * covers every component that mounts a recorder rather than one call site.
 * The parent is never handed an empty file — there is no downstream check
 * to forget.
 *************************************************************************** */
export const MIN_RECORDING_SIZE_BYTES = 1024;

export const EMPTY_RECORDING_MESSAGE = 'Nothing was recorded. Check that your microphone is working and try again.';

/* Ordered by preference; the first the browser supports wins. Opus is the
 * better codec and the smaller upload, so it leads. */
const CANDIDATE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  return CANDIDATE_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

/* Permissions-Policy can disable the microphone for the whole document. When
 * it does, the API surface stays fully intact — MediaRecorder exists,
 * getUserMedia is a function — and the call simply rejects with
 * NotAllowedError, with no prompt and nothing the user can change in their
 * browser settings. Feature detection alone therefore reports a working
 * recorder on an origin where recording can never succeed, which is how CP
 * shipped a menu item that failed and then told people to fix a setting that
 * was not the problem. Checked here so the control is hidden rather than
 * offered and then broken.
 *
 * Absence of the API is treated as ALLOWED, deliberately. Safari does not
 * expose document.featurePolicy at all, and a missing capability check must
 * not remove a feature that works — the recorder still fails safely through
 * its own getUserMedia error path if it turns out to be blocked. */
const isMicrophoneAllowedByPolicy = () => {
  try {
    if (typeof document === 'undefined' || !document.featurePolicy) return true;
    if (typeof document.featurePolicy.allowsFeature !== 'function') return true;
    return document.featurePolicy.allowsFeature('microphone');
  } catch (e) {
    return true;
  }
};

export const isRecordingSupported = () => (
  typeof MediaRecorder !== 'undefined'
  && typeof navigator !== 'undefined'
  && !!navigator.mediaDevices
  && typeof navigator.mediaDevices.getUserMedia === 'function'
  && isMicrophoneAllowedByPolicy()
);

export const formatSeconds = (seconds) => {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

const extensionFor = (mimeType) => {
  if (mimeType.includes('mp4')) return 'mp4';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
};

/* onError defaults to a no-op so the component can never be mounted into a
 * throw. It used to fire only when getUserMedia failed; since CON-1720 it
 * also fires for any empty recording, which is a far more common path, and
 * `isRequired` on the propType only warns in development. A no-op is the
 * honest default rather than a helpful one: the recorder has no way to show
 * a message itself, so a parent that omits onError gets a recorder that
 * closes silently on an empty recording. Surfacing the message is the
 * parent's job, and the two parents that exist both pass one. */
function VoiceRecorder({ onRecorded, onCancel, onError = () => {} }) {
  const [elapsed, setElapsed] = useState(0);
  const [isStopping, setIsStopping] = useState(false);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const bytesRef = useRef(0);
  // Which limit ended the recording: 'user' | 'time' | 'bytes'. Set immediately
  // before each stop() call and reported to the parent in onRecorded's meta, so
  // the parent can tell the user WHY it ended when it was not their doing. The
  // time limit announces itself with the amber countdown; the byte limit has no
  // such run-up and is designed never to fire — so when it does, silence would
  // read as "it broke".
  const stopReasonRef = useRef('user');
  const tickRef = useRef(null);
  const startedAtRef = useRef(null);
  // Set when the user cancels, so the onstop handler knows to throw the audio
  // away instead of handing it to the parent.
  const discardedRef = useRef(false);

  /* Release the microphone. Called from every exit path — stop, cancel,
   * unmount and failure — because a live getUserMedia stream keeps the
   * browser's recording indicator lit and the mic held open. */
  const releaseStream = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    setIsStopping(true);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    } else {
      releaseStream();
    }
  }, [releaseStream]);

  const cancel = useCallback(() => {
    discardedRef.current = true;
    stop();
    onCancel();
  }, [stop, onCancel]);

  /* Start on mount: the parent renders this component only once the user has
   * chosen Record a Voice Memo, so there is no separate idle state to sit in. */
  useEffect(() => {
    let cancelledBeforeStart = false;

    const begin = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelledBeforeStart) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        const mimeType = pickMimeType();
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        recorderRef.current = recorder;
        chunksRef.current = [];
        bytesRef.current = 0;
        stopReasonRef.current = 'user';

        recorder.ondataavailable = (e) => {
          const size = (e.data && e.data.size) || 0;
          if (size) chunksRef.current.push(e.data);
          bytesRef.current += size;
          // Byte limit — the second of the two limits, checked per chunk so
          // the recording ends BEFORE it is too big rather than being
          // refused after. See shouldStopForBytes.
          if (recorder.state !== 'inactive'
            && shouldStopForBytes({
              bytes: bytesRef.current, lastChunk: size, ceiling: MAX_RECORDING_SIZE_BYTES,
            })) {
            stopReasonRef.current = 'bytes';
            stop();
          }
        };

        recorder.onstop = () => {
          releaseStream();
          if (discardedRef.current) return;
          const type = recorder.mimeType || mimeType || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type });
          // CON-1720 — refuse an empty or header-only recording HERE, with the
          // real reason, rather than uploading it and letting ffmpeg fail on
          // it downstream. See MIN_RECORDING_SIZE_BYTES.
          if (blob.size < MIN_RECORDING_SIZE_BYTES) {
            onError(EMPTY_RECORDING_MESSAGE);
            return;
          }
          // A File rather than a Blob: the upload path reads .name, and the
          // size ceiling reads .type to decide which limit applies.
          const file = new File(
            [blob],
            `voice-memo-${Date.now()}.${extensionFor(type)}`,
            { type },
          );
          const seconds = startedAtRef.current
            ? (Date.now() - startedAtRef.current) / 1000
            : 0;
          onRecorded(file, { seconds, stoppedBy: stopReasonRef.current });
        };

        startedAtRef.current = Date.now();
        recorder.start(RECORDER_TIMESLICE_MS);

        tickRef.current = setInterval(() => {
          const seconds = (Date.now() - startedAtRef.current) / 1000;
          setElapsed(seconds);
          // Time limit — the first of the two limits. AUTO-STOP, not refuse:
          // the consultant keeps what they recorded. The byte limit lives in
          // ondataavailable; whichever trips first ends the recording.
          if (seconds >= MAX_RECORDING_SECONDS) {
            stopReasonRef.current = 'time';
            stop();
          }
        }, 200);
      } catch (e) {
        releaseStream();
        onError(
          e && (e.name === 'NotAllowedError' || e.name === 'SecurityError')
            ? 'Microphone access was blocked. Allow it in your browser settings and try again.'
            : 'Could not start recording. Check that a microphone is connected.',
        );
      }
    };

    begin();

    return () => {
      cancelledBeforeStart = true;
      discardedRef.current = true;
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      releaseStream();
    };
    // Mount only. Re-running would start a second recorder on the same stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remaining = Math.max(0, MAX_RECORDING_SECONDS - elapsed);
  const isWarning = remaining <= WARN_REMAINING_SECONDS;
  const percent = Math.min(100, (elapsed / MAX_RECORDING_SECONDS) * 100);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: '8px',
        border: '1px solid #e0e0e0',
        backgroundColor: '#fff',
        width: '100%',
      }}
    >
      <MicIcon
        fontSize="small"
        sx={{
          color: isWarning ? 'warning.main' : 'error.main',
          // A recording indicator that is visibly live, so a forgotten
          // recording is obvious rather than silently running.
          animation: 'cpRecPulse 1.4s ease-in-out infinite',
          '@keyframes cpRecPulse': { '50%': { opacity: 0.25 } },
        }}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
          >
            {formatSeconds(elapsed)}
          </Typography>
          <Typography
            variant="caption"
            color={isWarning ? 'warning.main' : 'text.secondary'}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {isWarning
              ? `${Math.ceil(remaining)}s left`
              : `of ${formatSeconds(MAX_RECORDING_SECONDS)} max`}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percent}
          color={isWarning ? 'warning' : 'primary'}
          sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
        />
      </Box>

      <Tooltip title="Stop recording">
        <span>
          <IconButton size="small" onClick={stop} disabled={isStopping} color="primary">
            <StopCircleIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Discard">
        <span>
          <IconButton size="small" onClick={cancel} disabled={isStopping}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

VoiceRecorder.propTypes = {
  /** Called with (File, { seconds, stoppedBy: 'user'|'time'|'bytes' }) once recording stops normally. */
  onRecorded: PropTypes.func.isRequired,
  /** Called when the user discards; no file is produced. */
  onCancel: PropTypes.func.isRequired,
  /** Called with a human-readable string when recording cannot start. */
  onError: PropTypes.func,
};

export default VoiceRecorder;
